/**
 * Abort-reason vocabulary for the Evidence chat SSE stream.
 *
 * Kept in its own dependency-free module (no app imports) so the light hooks
 * that only need to *stamp* a reason — `useGlobalStreamTimeoutWatcher`,
 * `useChatStreamControl` — don't drag in the heavy `timelineEventSource` graph
 * (store, API, region config). `timelineEventSource` re-exports these so
 * existing importers keep working.
 */

/**
 * Why a stream's `AbortController` was tripped. `closeReason: 'abort'` alone
 * collapses every programmatic abort into one bucket, so a spike of aborts in
 * RUM can't be told apart — a benign user cancel looks identical to a supersede
 * storm or a retry loop. Each abort site stamps one of these on the signal so
 * the terminal report can attribute the cause.
 *
 * Bounded and free of user input, so it is PHI-safe. Carried **only** on the
 * RUM lifecycle (`abort_reason`), never on the Snowplow `evidence_chat_error`
 * event — so it needs no iglu schema change.
 */
export const STREAM_ABORT_REASON = {
  /** User pressed stop. */
  USER_CANCEL: 'user_cancel',
  /** Non-user programmatic abort (e.g. teardown), not a user cancel. */
  PROGRAMMATIC: 'programmatic',
  /** A newer send replaced this in-flight stream. */
  SUPERSEDED: 'superseded',
  /** Retry-last-message tore down the prior stream before re-sending. */
  RETRY: 'retry',
  /** An unexpected chunk-processing error forced the stream down. */
  CHUNK_PROCESS_ERROR: 'chunk_process_error',
  /** The global stuck-stream watchdog aborted a dead/stalled connection. */
  GLOBAL_TIMEOUT: 'global_timeout',
} as const;

export type StreamAbortReason =
  (typeof STREAM_ABORT_REASON)[keyof typeof STREAM_ABORT_REASON];

/**
 * Abort a stream while recording *why* on the native `AbortSignal.reason` — a
 * lossless channel that already exists, so no new field is threaded through the
 * call graph. The reason is wrapped in a `DOMException` named `AbortError` so
 * existing `AbortError`-based classification stays unchanged; the bounded reason
 * rides along in its `message`.
 */
export const abortStreamWithReason = (
  controller: AbortController,
  reason: StreamAbortReason
): void => controller.abort(new DOMException(reason, 'AbortError'));

const KNOWN_ABORT_REASONS: ReadonlySet<string> = new Set(
  Object.values(STREAM_ABORT_REASON)
);

/**
 * Reads back the bounded {@link StreamAbortReason} stamped by
 * {@link abortStreamWithReason}. Returns `undefined` for a bare `abort()` (whose
 * default reason is not one of ours) or any non-abort settle, so the field is
 * only present when we genuinely know the cause.
 */
export const deriveAbortReason = (signal: AbortSignal): StreamAbortReason | undefined => {
  const reason: unknown = signal.reason;
  const message =
    reason instanceof DOMException
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : undefined;
  return message !== undefined && KNOWN_ABORT_REASONS.has(message)
    ? (message as StreamAbortReason)
    : undefined;
};
